/**
 * CompareTab — Side-by-side entity comparison with field-level diff highlighting
 *
 * Allows users to select a second entity of the same primitive type
 * and compare fields side by side. Changed fields are highlighted.
 *
 * DUMB FRONTEND: Entity data comes from backend GraphQL.
 */

import { useState, useMemo } from 'react';
import {
  Stack,
  Text,
  Group,
  Badge,
  Center,
  Paper,
  Select,
  Table,
  ThemeIcon,
  Alert,
} from '@mantine/core';
import {
  IconArrowsExchange,
  IconEqual,
  IconArrowRight,
} from '@tabler/icons-react';
import { formatFieldName, FieldValue } from '@/components/RecordDetailDrawer';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// ============================================================================
// TYPES
// ============================================================================

interface CompareTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
  /** All entities of the same type available for comparison */
  allEntities?: PrimitiveItem[];
}

interface FieldDiff {
  key: string;
  leftValue: unknown;
  rightValue: unknown;
  changed: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

/** Fields to exclude from comparison (internal / metadata) */
const EXCLUDED_FIELDS = new Set(['__typename', '_viewedAt']);

function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  // Deep compare for arrays/objects via JSON (sufficient for display purposes)
  return JSON.stringify(a) === JSON.stringify(b);
}

function computeDiffs(left: PrimitiveItem, right: PrimitiveItem): FieldDiff[] {
  const allKeys = new Set([
    ...Object.keys(left),
    ...Object.keys(right),
  ]);

  const diffs: FieldDiff[] = [];
  for (const key of allKeys) {
    if (key.startsWith('_') || EXCLUDED_FIELDS.has(key)) continue;
    const leftValue = left[key];
    const rightValue = right[key];
    diffs.push({
      key,
      leftValue,
      rightValue,
      changed: !isEqual(leftValue, rightValue),
    });
  }

  // Sort: changed fields first, then alphabetically
  return diffs.sort((a, b) => {
    if (a.changed !== b.changed) return a.changed ? -1 : 1;
    return a.key.localeCompare(b.key);
  });
}

// ============================================================================
// COMPONENT
// ============================================================================

export function CompareTab({ entity, primitiveType, allEntities }: CompareTabProps) {
  const [compareEntityId, setCompareEntityId] = useState<string | null>(null);

  // Build selector options (exclude current entity)
  const selectorOptions = useMemo(() => {
    if (!allEntities || !entity) return [];
    const currentId = entity.id as string;
    return allEntities
      .filter(e => (e.id as string) !== currentId)
      .map(e => ({
        value: e.id as string,
        label: `${(e.name as string) || (e.id as string)}${e.status ? ` (${e.status})` : ''}`,
      }));
  }, [allEntities, entity]);

  // Find the compare target entity
  const compareEntity = useMemo(() => {
    if (!compareEntityId || !allEntities) return null;
    return allEntities.find(e => (e.id as string) === compareEntityId) ?? null;
  }, [compareEntityId, allEntities]);

  // Compute field diffs
  const diffs = useMemo(() => {
    if (!entity || !compareEntity) return [];
    return computeDiffs(entity, compareEntity);
  }, [entity, compareEntity]);

  const changedCount = diffs.filter(d => d.changed).length;

  if (!entity) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select an entity to compare.</Text>
      </Center>
    );
  }

  if (!allEntities || allEntities.length < 2) {
    return (
      <Alert color="blue" title="Compare">
        <Text size="sm">
          Need at least two entities of type &ldquo;{primitiveType}&rdquo; to compare.
          Load more data or remove filters to enable comparison.
        </Text>
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {/* Entity selector */}
      <Paper withBorder p="sm" radius="sm">
        <Group gap="sm" align="flex-end">
          <Stack gap={2} style={{ flex: 1 }}>
            <Text size="xs" fw={600}>Current Entity</Text>
            <Badge size="sm" variant="light">
              {(entity.name as string) || (entity.id as string)}
            </Badge>
          </Stack>

          <ThemeIcon size="md" variant="light" color="gray">
            <IconArrowsExchange size={16} />
          </ThemeIcon>

          <Select
            label="Compare With"
            placeholder="Select entity..."
            data={selectorOptions}
            value={compareEntityId}
            onChange={setCompareEntityId}
            searchable
            clearable
            style={{ flex: 1 }}
            size="xs"
          />
        </Group>
      </Paper>

      {/* Diff summary */}
      {compareEntity && (
        <Group gap="xs">
          <Badge size="sm" variant="light" color={changedCount > 0 ? 'orange' : 'green'}>
            {changedCount} field{changedCount !== 1 ? 's' : ''} differ
          </Badge>
          <Badge size="sm" variant="light" color="gray">
            {diffs.length - changedCount} identical
          </Badge>
        </Group>
      )}

      {/* Diff table */}
      {compareEntity && diffs.length > 0 && (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={140}>Field</Table.Th>
              <Table.Th>
                <Text size="xs" c="dimmed">Current</Text>
              </Table.Th>
              <Table.Th w={30} />
              <Table.Th>
                <Text size="xs" c="dimmed">Compare</Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {diffs.map(diff => (
              <Table.Tr
                key={diff.key}
                style={diff.changed ? {
                  backgroundColor: 'var(--mantine-color-orange-light)',
                } : undefined}
              >
                <Table.Td fw={600} style={{ textTransform: 'capitalize' }}>
                  {formatFieldName(diff.key)}
                </Table.Td>
                <Table.Td>
                  <FieldValue value={diff.leftValue} fieldKey={diff.key} />
                </Table.Td>
                <Table.Td>
                  {diff.changed ? (
                    <IconArrowRight size={12} color="var(--mantine-color-orange-6)" />
                  ) : (
                    <IconEqual size={12} color="var(--mantine-color-dimmed)" />
                  )}
                </Table.Td>
                <Table.Td>
                  <FieldValue value={diff.rightValue} fieldKey={diff.key} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* No comparison selected */}
      {!compareEntity && (
        <Center py="lg">
          <Stack align="center" gap="sm">
            <IconArrowsExchange size={32} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed" size="sm">
              Select an entity above to compare side by side.
            </Text>
          </Stack>
        </Center>
      )}
    </Stack>
  );
}
